'use client';

import Link from 'next/link';
import Image from 'next/image';
import ReactMarkdown from 'react-markdown';
import { Evento, Iniziativa } from '@/types';
import { LABELS } from '@/lib/labels';
import { useShare } from '@/lib/useShare';
import { getSportImage } from '@/lib/sportUtils';
import { getSpazioImage, getSpazioText } from '@/lib/spazioUtils';

interface Props {
  evento: Evento;
  iniziativa: Iniziativa;
}

export default function EventoDetails({ evento, iniziativa }: Props) {
  const sportImage = getSportImage(evento);
  const { share } = useShare(evento.titolo, evento.descrizioneBreve);

  const menuEffettivo = evento.menuSerata ?? iniziativa.menuSerata ?? [];
  const spaziEffettivi = evento.spaziAperti ?? iniziativa.spaziAperti;

  const descrizioneText = Array.isArray(evento.descrizioneCompleta)
    ? evento.descrizioneCompleta.join('\n\n')
    : evento.descrizioneCompleta;

  const noteSource = evento.noteOrganizzative ?? (evento.stato !== 'non-attivo' ? iniziativa.noteStandard : undefined);
  const noteItems = noteSource
    ? noteSource
        .split('\n')
        .map((l) => l.replace(/^[-*•✓]\s*/, '').trim())
        .filter(Boolean)
    : [];

  return (
    <div className="bg-board-dark">
      <div className="max-w-4xl mx-auto">

        {/* 1. COSA TI ASPETTA */}
        <section className="px-4 pt-8 pb-0" aria-labelledby="heading-aspetta">
          <div className="bg-board-light rounded-2xl p-6 md:p-8 border border-chalk/10 shadow-sm">
            <div className="flex items-start gap-5">
              <div className="hidden sm:block flex-shrink-0 mt-1" aria-hidden="true">
                <Image src={sportImage} alt="Icona sport" width={64} height={64} className="object-contain" />
              </div>
              <div>
                <h2
                  id="heading-aspetta"
                  className="font-section text-accent-orange text-base md:text-lg uppercase tracking-wide mb-3"
                >
                  {LABELS.evento.cosaTiAspetta}
                </h2>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <p className="text-chalk-dim text-sm md:text-base leading-relaxed mb-2 last:mb-0">{children}</p>
                    ),
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-orange underline hover:opacity-80"
                      >
                        {children}
                      </a>
                    ),
                    ul: ({ children }) => (
                      <ul className="my-3 list-disc space-y-1 pl-5 marker:text-accent-orange">{children}</ul>
                    ),
                    li: ({ children }) => <li className="pl-1">{children}</li>,
                  }}
                >
                  {descrizioneText}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </section>

        {/* 2. PROIEZIONE SPORTIVA + SPAZI DISPONIBILI */}
        {evento.stato !== 'non-attivo' && <section className="px-4 pt-5 pb-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Proiezione */}
            <div className="bg-section-green rounded-2xl border border-chalk/10">
              <div>
                <div className="relative w-full aspect-[4/4] rounded-xl overflow-hidden">
                  <Image
                    src={evento.immagineEventoSportivo ?? '/_images/placeholder-proiezione-transparent.webp'}
                    alt={evento.immagineEventoSportivo ? (evento.altImgEventoSportivo ?? LABELS.evento.proiezioneDettagli) : LABELS.evento.proiezionePlaceholderAlt}
                    fill
                    className="object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Spazi disponibili */}
            <div className="bg-section-blue rounded-2xl p-6">
              <h2 className="font-section text-chalk text-base uppercase tracking-wide mb-4 text-center">
                {LABELS.evento.titoloSpaziDisponibili}
              </h2>
              <div className="grid grid-cols-3 gap-3">
                {spaziEffettivi.map((spazio, i) => (
                  <div key={i} className="flex flex-col items-center gap-2 p-3 rounded-xl">
                    <Image src={getSpazioImage(spazio)} alt={getSpazioText(spazio)} width={48} height={48} className="object-contain" aria-hidden="true" />
                    <span className="text-chalk text-[10px] text-center leading-snug font-medium">{getSpazioText(spazio)}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </section>}

        {/* 3. CIBO & BEVANDE */}
        {evento.stato !== 'non-attivo' && (menuEffettivo.length > 0 || evento.notaCibo) ? (
          <section className="px-4 pt-5 pb-0" aria-labelledby="heading-cibo">
            <div className="bg-section-orange rounded-2xl p-6 md:p-8">
              <h2
                id="heading-cibo"
                className="font-bold text-chalk text-base uppercase tracking-wide mb-5 flex items-center justify-center gap-2"
              >
                {LABELS.evento.titoloCiboBevande}
              </h2>
              {menuEffettivo.length > 0 ? (
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="flex items-end gap-3 flex-wrap justify-center flex-shrink-0">
                    {menuEffettivo.map((item, i) => (
                      <div key={i} className="flex flex-col items-center gap-1">
                        {item.immagine ? (
                          <div className="relative w-20 h-20 md:w-24 md:h-24">
                            <Image
                              src={item.immagine}
                              alt={item.nome}
                              fill
                              className="object-contain drop-shadow-lg"
                            />
                          </div>
                        ) : (
                          <Image
                            src="/_images/icons/bibita.webp"
                            alt={item.nome}
                            width={80}
                            height={80}
                            className="object-contain drop-shadow-lg"
                          />
                        )}
                        {item.prezzo && (
                          <span className="text-chalk font-bold text-sm">{item.prezzo}</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-chalk-dim text-sm md:text-base leading-relaxed text-center md:text-left">
                    {`Sono disponibili ${menuEffettivo.map(i => i.nome).join(', ')} e bevande a prezzi accessibili!`}
                  </p>
                </div>
              ) : (
                <p className="text-chalk-dim text-sm md:text-base leading-relaxed text-center">
                  {evento.notaCibo}
                </p>
              )}
            </div>
          </section>
        ) : null}

        {/* 4. NOTE ORGANIZZATIVE */}
        {noteItems.length > 0 && (
          <section className="px-4 pt-5 pb-0" aria-labelledby="heading-note">
            <div className="bg-board-light rounded-2xl p-6 md:p-8 border border-chalk/10">
              <h2
                id="heading-note"
                className="font-bold text-chalk text-base uppercase tracking-wide mb-4 flex items-center gap-2"
              >
                <Image src="/_images/icons/note.webp" alt="Note organizzative" width={24} height={24} className="object-contain" aria-hidden="true" /> {LABELS.evento.titoloNoteOrganizzative}
              </h2>
              <ul className="space-y-2">
                {noteItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-chalk-dim text-sm">
                    <span className="text-accent-green mt-0.5 flex-shrink-0 font-bold" aria-hidden="true">
                      ✓
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Chiusura tematica */}
        {evento.stato !== 'non-attivo' && menuEffettivo.length > 0 && !evento.notaCibo ? (
        <div className="px-4 pt-8 pb-2 text-center">
          <p className="font-bold text-chalk text-base md:text-lg leading-relaxed">
            Non mancare! Porta i tuoi amici e vivi insieme
            <br className="hidden sm:block" /> una serata indimenticabile all&rsquo;oratorio!
          </p>
        </div>
        ) : null}

        {/* 5. CTA Bar */}
        <div className="px-4 pt-5 pb-10">
          <div className="flex flex-col sm:flex-row items-stretch gap-3">
            <Link
              href="/calendario/"
              className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-chalk text-chalk font-bold px-6 py-3 rounded-xl hover:bg-chalk hover:text-board transition-colors text-sm uppercase tracking-wide"
            >
              <span aria-hidden="true">←</span> {LABELS.evento.tornaCalendario}
            </Link>
            <button
              onClick={share}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-accent-orange text-white font-bold px-6 py-3 rounded-xl hover:bg-accent-gold transition-colors text-sm uppercase tracking-wide"
            >
              <span aria-hidden="true">🔗</span> {LABELS.evento.condividiSerata}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
